<template>
    <div>
        <div :data-type="type" class="form-item-radio-input"
             :class="wrapperClass">
            <label :for="'form-global-' + fieldId"
                   v-if="label">
                <span v-if="required">* </span>{{ label }}
            </label>
            <!--  :class="elementClass" -->
            <div v-if="question.optionHtml === 'default'"
                 class="btn-group d-flex"
                 :class="[question.optionOrder === 'column' || question.optionOrder === '0' ? 'flex-column' : 'flex-wrap']">
                <div v-for="(item, optionValue) in options" :key="optionValue" class="form-check pe-3">
                    <input
                        :id="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue"
                        :class="[inputClass, { 'is-invalid': shouldShowError }]"
                        :name="name"
                        :type="type"
                        :value="optionValue"
                        :checked="isOptionSelected(optionValue)"
                        @change="handleRadioChange(optionValue)" />

                    <label :for="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue" class="form-check-label" v-html="item.label">
                    </label>

                    <div
                        v-if="item.additionalInfo != ''"
                        class="form-input-help mb-1"
                        v-html="item.additionalInfo"
                    >
                    </div>

                    <!-- Campo de texto para "other" -->
                    <div v-if="optionValue === 'other' && selectedValue == 'other'" class="mt-2">
                        <input
                            type="text"
                            class="form-control question-input-text-other"
                            :class="[{ 'is-invalid': shouldShowError }]"
                            :placeholder="otherText || 'Especificar...'"
                            v-model="otherValue"
                            :id="'form-global-' + fieldId"
                            :name="'question_' + typeFieldId + '_' + fieldId + '_' + otherId + '_text_other'"
                            :disabled="!isOptionSelected('other')"
                            @input="handleOtherTextChange($event.target.value)" />
                    </div>
                </div>
            </div>
            <div class="btn-group d-flex"
                 :class="[question.optionOrder === 'column' || question.optionOrder === '0' ? 'flex-column' : 'flex-wrap']"
                 v-if="question.optionHtml === 'button'">
                <div v-for="(item, optionValue) in options" :key="optionValue" class="form-check">
                    <input
                        :id="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue"
                        :name="name"
                        :type="type"
                        :value="optionValue"
                        :checked="isOptionSelected(optionValue)"
                        @change="handleRadioChange(optionValue)" />

                    <label class="btn btn-outline-primary btn-check-icon-outline label-options"
                           :for="'form-global-' + typeFieldId + '_' + fieldId + '_' + optionValue" v-html="item.label">
                    </label>

                    <div
                        v-if="item.additionalInfo != ''"
                        class="form-input-help mb-1"
                        v-html="item.additionalInfo"
                    >
                    </div>

                    <!-- Campo de texto para "other" -->
                    <div v-if="optionValue === 'other' && selectedValue == 'other'" class="mt-2">fieldId
                        <input
                            type="text"
                            class="form-control question-input-text-other"
                            :class="[{ 'is-invalid': shouldShowError }]"
                            :placeholder="otherText || 'Especificar...'"
                            v-model="otherValue"
                            :id="'form-global-' + fieldId"
                            :name="'question_' + typeFieldId + '_' + fieldId + '_' + otherId + '_text_other'"
                            :disabled="!isOptionSelected('other')"
                            @input="handleOtherTextChange($event.target.value)" />
                    </div>
                </div>
            </div>
        </div>
        <div v-if="shouldShowError" class="has-error-msg invalid-feedback d-block">
            {{ errorMessage }}
        </div>
    </div>
</template>

<script setup>
import { computed, watch, ref, onMounted } from 'vue';
import { useField } from 'vee-validate';
import { useTranslation } from '@/composables/useTranslation';

// INITIALIZE STORE & COMPOSABLES
const { translate } = useTranslation();

// PROPS & EMITS
const props = defineProps({
    elementClass: { type: String, required: false, default: '' },
    errorText: { type: String, required: false },
    fieldId: { type: Number, required: true },
    inputClass: { type: String, required: false },
    label: { type: String, required: false, default: '' },
    name: { type: String, required: true },
    options: { type: Object, required: true },
    otherId: { type: Number, required: false, default: 0 },
    otherText: { type: String, required: false },
    question: { type: Object, required: true },
    required: { type: Number, required: false },
    type: { type: String, required: true },
    typeFieldId: { type: Number, required: true },
    validation: { type: Object, required: false },
    wrapperClass: { type: String, required: false, default: '' },
    hasSubmitted: { type: Boolean, default: false }
});

const emit = defineEmits(['update:modelAnswer', 'register-field', 'field-change', 'other-selected']);

// REACTIVE VARIABLES
const selectedValue = ref('');
const otherValue = ref('');
const previousOtherValue = ref('');

// COMPUTED PROPERTIES (LA MAGIA ESTÁ AQUÍ)
const validationRules = computed(() => {
    // 1. Extraemos las variables para forzar a Vue a rastrearlas
    const currentSelected = selectedValue.value;
    const currentOtherText = otherValue.value;
    const isRequired = props.validation?.required;
    const customErrorText = props.errorText;

    return (val) => {
        // Validación 1: ¿Es requerido y no hay nada seleccionado?
        if (isRequired && (!currentSelected || currentSelected.toString().trim() === '')) {
            return customErrorText || translate('label.this_value_is_required') || 'This field is required';
        }

        // Validación 2: Si seleccionó 'other', exigir que haya texto
        if (currentSelected === 'other') {
            if (!currentOtherText || currentOtherText.trim() === '') {
                return translate('label.please_specify_other') || 'This field is required';
            }
        }

        return true;
    };
});

// Configurar useField (extraemos setValue y validate)
const { value, errorMessage, meta, setValue, validate: validateField } = useField(props.name, validationRules, {
    initialValue: props.question.answer || '',
    validateOnValueUpdate: false,
    validateOnMount: false,
});

// Solo mostrar errores después del primer submit
const shouldShowError = computed(() => {
    return props.hasSubmitted && !!errorMessage.value;
});

// METHODS
const handleRadioChange = async (optionValue) => {
    selectedValue.value = optionValue;
    let valToSet = optionValue;

    if (optionValue === 'other') {
        otherValue.value = previousOtherValue.value;
        const finalValue = otherValue.value || '';
        valToSet = finalValue;

        emit('update:modelAnswer', 'other');
        emit('field-change', props.name, 'other');
        emit('other-selected', { optionId: props.otherId, otherValue: finalValue, isSelected: true });
    } else {
        if (otherValue.value && selectedValue.value === 'other') {
            previousOtherValue.value = otherValue.value;
        }

        otherValue.value = '';
        emit('update:modelAnswer', optionValue);
        emit('field-change', props.name, optionValue);
        emit('other-selected', { optionId: props.otherId, otherValue: '', isSelected: false });
    }

    // Usamos setValue en lugar de mutar value.value directamente
    setValue(valToSet);

    // Revalidar si ya hubo un error previo
    if (props.hasSubmitted) {
        await validateField();
    }
};

const handleOtherTextChange = async (newText) => {
    otherValue.value = newText;
    previousOtherValue.value = newText;

    if (selectedValue.value === 'other') {
        setValue(newText);
        emit('update:modelAnswer', 'other');
        emit('field-change', props.name, 'other');

        emit('other-selected', {
            optionId: props.otherId,
            otherValue: newText,
            isSelected: true
        });

        // Revalida mientras el usuario escribe
        if (props.hasSubmitted) {
            await validateField();
        }
    }
};

const isOptionSelected = (optionValue) => {
    return selectedValue.value === optionValue;
};

// LIFECYCLE HOOKS
onMounted(() => {
    const initialValue = props.question.answer || '';
    emit('register-field', props.name, initialValue);
});

// Sincronizar con el valor externo
watch(() => props.question.answer, (newValue) => {
    const stringValue = newValue ? newValue.toString() : '';
    const isKnownOption = Object.keys(props.options).includes(stringValue);

    if (isKnownOption) {
        selectedValue.value = stringValue;
        setValue(stringValue);
    } else if (stringValue) {
        selectedValue.value = 'other';
        otherValue.value = stringValue;
        previousOtherValue.value = stringValue;
        setValue(stringValue);
    } else {
        selectedValue.value = '';
        otherValue.value = '';
        previousOtherValue.value = '';
        setValue('');
    }
}, { immediate: true });

watch(() => props.question.answerOther, (newValue) => {
    otherValue.value = newValue || '';
}, { immediate: true });

</script>
